import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import MainLayout from '../layouts/MainLayout';
import { Plus, Edit, Eye, Upload, FileAudio, Trash2, X } from 'lucide-react';
import * as podcastService from '../services/podcastService';

interface PodcastItem {
  id?: string;
  _id?: string;
  slug?: string;
  title: {
    tr: string;
    en: string;
    es: string;
  };
  summary: {
    tr: string;
    en: string;
    es: string;
  };
  status: 'published' | 'draft';
  createdAt?: string;
  updatedAt?: string;
  // Podcast specific
  audioUrl?: string;
  thumbnailUrl?: string;
  duration?: string;
  tags?: string[];
  author?: string;
}

const dummyPodcasts: PodcastItem[] = [
  {
    id: '1',
    title: {
      tr: 'Su Yönetimi Teknolojileri',
      en: 'Water Management Technologies',
      es: 'Tecnologías de Gestión del Agua'
    },
    summary: {
      tr: 'Modern su yönetimi sistemleri hakkında detaylı bilgiler...',
      en: 'Detailed information about modern water management systems...',
      es: 'Información detallada sobre sistemas modernos de gestión del agua...'
    },
    status: 'published',
    createdAt: '2024-01-15T10:30:00Z',
    audioUrl: 'https://example.com/podcast1.mp3',
    duration: '25:30'
  },
  {
    id: '2',
    title: {
      tr: 'PE Boru Sistemleri',
      en: 'PE Pipe Systems',
      es: 'Sistemas de Tuberías PE'
    },
    summary: {
      tr: 'PE boru sistemlerinin avantajları ve kullanım alanları...',
      en: 'Advantages and applications of PE pipe systems...',
      es: 'Ventajas y aplicaciones de los sistemas de tuberías PE...'
    },
    status: 'draft',
    createdAt: '2024-01-20T14:15:00Z',
    audioUrl: 'https://example.com/podcast2.mp3',
    duration: '18:45'
  }
];

const Podcast: React.FC = () => {
  const [podcasts, setPodcasts] = useState<PodcastItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPodcast, setEditingPodcast] = useState<PodcastItem | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<'tr' | 'en' | 'es'>('tr');
  const [submitting, setSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [audioPreview, setAudioPreview] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<{
    thumbnail?: File;
    audio?: File;
  }>({});
  const [audioRemoved, setAudioRemoved] = useState(false);
  const [imageRemoved, setImageRemoved] = useState(false);

  // Her dil için ayrı form state'leri
  const [formTitles, setFormTitles] = useState({ tr: '', en: '', es: '' });
  const [formSummaries, setFormSummaries] = useState({ tr: '', en: '', es: '' });

  // Podcast'leri API'den yükle
  const fetchPodcasts = async () => {
    try {
      setLoading(true);
      const podcastsData = await podcastService.getPodcasts();
      console.log('Podcasts received:', podcastsData);
      setPodcasts(podcastsData);
    } catch (error) {
      console.error('Error fetching podcasts:', error);
      toast.error('Podcast\'ler yüklenirken hata oluştu');
      setPodcasts(dummyPodcasts); // Fallback to dummy data
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPodcasts();
  }, []);

  const handleEdit = (podcast: PodcastItem) => {
    // Normalize podcast data
    const normalizedPodcast = {
      ...podcast,
      id: podcast.id || podcast._id,
      title: podcast.title || { tr: '', en: '', es: '' },
      summary: podcast.summary || { tr: '', en: '', es: '' },
      status: podcast.status || 'draft'
    };
    setEditingPodcast(normalizedPodcast);
    
    // Form state'lerini güncelle
    setFormTitles({
      tr: normalizedPodcast.title.tr || '',
      en: normalizedPodcast.title.en || '',
      es: normalizedPodcast.title.es || ''
    });
    setFormSummaries({
      tr: normalizedPodcast.summary.tr || '',
      en: normalizedPodcast.summary.en || '',
      es: normalizedPodcast.summary.es || ''
    });
    
    // Dili TR'ye ayarla
    setSelectedLanguage('tr');
    
    // Mevcut görsel ve ses varsa önizleme olarak ayarla, yoksa temizle
    if (podcast.thumbnailUrl) {
      setImagePreview(podcast.thumbnailUrl);
    } else {
      setImagePreview(null);
    }
    
    if (podcast.audioUrl) {
      setAudioPreview(podcast.audioUrl);
    } else {
      setAudioPreview(null);
    }
    
    // Selected files'ı temizle
    setSelectedFiles({});
    
    // Silme durumlarını sıfırla
    setAudioRemoved(false);
    setImageRemoved(false);
    
    setShowForm(true);
  };

  const handleNewPodcast = () => {
    setEditingPodcast(null);
    
    // Form state'lerini temizle
    setFormTitles({ tr: '', en: '', es: '' });
    setFormSummaries({ tr: '', en: '', es: '' });
    
    // Dili TR'ye ayarla
    setSelectedLanguage('tr');
    
    setImagePreview(null);
    setAudioPreview(null);
    setSelectedFiles({});
    
    // Silme durumlarını sıfırla
    setAudioRemoved(false);
    setImageRemoved(false);
    
    setShowForm(true);
  };

  const handleCloseForm = () => {
    // Blob URL'leri temizle
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    if (audioPreview && audioPreview.startsWith('blob:')) {
      URL.revokeObjectURL(audioPreview);
    }
    
    // Form state'lerini temizle
    setFormTitles({ tr: '', en: '', es: '' });
    setFormSummaries({ tr: '', en: '', es: '' });
    
    setShowForm(false);
    setEditingPodcast(null);
    setImagePreview(null);
    setAudioPreview(null);
    setSelectedFiles({});
    setSelectedLanguage('tr');
    setAudioRemoved(false);
    setImageRemoved(false);
  };

  // Dosya yükleme fonksiyonları
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Dosya boyutu kontrolü (2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Görsel dosya boyutu 2MB\'dan küçük olmalıdır');
        return;
      }

      // Dosya tipi kontrolü
      if (!file.type.startsWith('image/')) {
        toast.error('Sadece görsel dosyaları yükleyebilirsiniz');
        return;
      }

      // Eski blob URL'yi temizle
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }

      // Yeni önizleme oluştur
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      setSelectedFiles(prev => ({ ...prev, thumbnail: file }));
    }
  };

  const handleAudioSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Dosya boyutu kontrolü (50MB)
      if (file.size > 50 * 1024 * 1024) {
        toast.error('Ses dosya boyutu 50MB\'dan küçük olmalıdır');
        return;
      }

      // Dosya tipi kontrolü
      if (!file.type.startsWith('audio/')) {
        toast.error('Sadece ses dosyaları yükleyebilirsiniz');
        return;
      }

      // Eski blob URL'yi temizle
      if (audioPreview && audioPreview.startsWith('blob:')) {
        URL.revokeObjectURL(audioPreview);
      }

      // Yeni önizleme oluştur
      const previewUrl = URL.createObjectURL(file);
      setAudioPreview(previewUrl);
      setSelectedFiles(prev => ({ ...prev, audio: file }));
    }
  };

  const handleRemoveImage = () => {
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
    setSelectedFiles(prev => ({ ...prev, thumbnail: undefined }));
    setImageRemoved(true); // Görsel silindiğini işaretle
  };

  const handleRemoveAudio = () => {
    if (audioPreview && audioPreview.startsWith('blob:')) {
      URL.revokeObjectURL(audioPreview);
    }
    setAudioPreview(null);
    setSelectedFiles(prev => ({ ...prev, audio: undefined }));
    setAudioRemoved(true); // Ses silindiğini işaretle
  };

  const handleFileUploads = async (podcastId: string) => {
    let success = true;
    
    // Thumbnail yükleme
    if (selectedFiles.thumbnail) {
      try {
        console.log("Uploading podcast thumbnail for ID:", podcastId);
        await podcastService.uploadPodcastThumbnail(podcastId, selectedFiles.thumbnail);
        console.log("Podcast thumbnail uploaded successfully");
      } catch (error) {
        console.error("Error uploading podcast thumbnail:", error);
        toast.error('Kapak görseli yüklenirken hata oluştu');
        success = false;
      }
    }

    // Audio yükleme
    if (selectedFiles.audio) {
      try {
        console.log("Uploading podcast audio for ID:", podcastId);
        await podcastService.uploadPodcastAudio(podcastId, selectedFiles.audio);
        console.log("Podcast audio uploaded successfully");
      } catch (error) {
        console.error("Error uploading podcast audio:", error);
        toast.error('Ses dosyası yüklenirken hata oluştu');
        success = false;
      }
    }

    return success;
  };

  const handleDelete = async (podcast: PodcastItem) => {
    if (window.confirm('Bu podcast\'i silmek istediğinizden emin misiniz?')) {
      try {
        const podcastId = podcast.id || podcast._id;
        if (!podcastId) {
          toast.error('Podcast ID bulunamadı');
          return;
        }
        await podcastService.deletePodcast(podcastId);
        toast.success('Podcast başarıyla silindi');
        fetchPodcasts();
      } catch (error) {
        console.error('Error deleting podcast:', error);
        toast.error('Podcast silinirken hata oluştu');
      }
    }
  };

  const handleSubmit = async (formData: FormData) => {
    try {
      setSubmitting(true);
      
      const podcastData = {
        title: {
          tr: formTitles.tr || '',
          en: formTitles.en || '',
          es: formTitles.es || '',
        },
        summary: {
          tr: formSummaries.tr || '',
          en: formSummaries.en || '',
          es: formSummaries.es || '',
        },
        status: formData.get('status') as string || 'draft'
      };

      let podcastId;
      if (editingPodcast) {
        const id = editingPodcast.id || editingPodcast._id;
        if (!id) {
          toast.error('Podcast ID bulunamadı');
          return;
        }
        await podcastService.updatePodcast(id, podcastData);
        podcastId = id;
        toast.success('Podcast başarıyla güncellendi');
      } else {
        const result = await podcastService.createPodcast(podcastData);
        podcastId = result._id || result.id;
        toast.success('Podcast başarıyla oluşturuldu');
      }

      // Dosya silme işlemleri (önce silme, sonra yükleme)
      if (podcastId && editingPodcast) {
        if (audioRemoved) {
          try {
            await podcastService.removePodcastAudio(podcastId);
            console.log('Podcast audio removed successfully');
          } catch (error) {
            console.error('Error removing podcast audio:', error);
            toast.error('Ses dosyası silinirken hata oluştu');
          }
        }
        
        if (imageRemoved) {
          try {
            await podcastService.removePodcastThumbnail(podcastId);
            console.log('Podcast thumbnail removed successfully');
          } catch (error) {
            console.error('Error removing podcast thumbnail:', error);
            toast.error('Kapak görseli silinirken hata oluştu');
          }
        }
      }

      // Dosya yüklemeleri
      if (podcastId && (selectedFiles.thumbnail || selectedFiles.audio)) {
        const uploadSuccess = await handleFileUploads(podcastId);
        if (!uploadSuccess) {
          toast.error('Podcast kaydedildi ancak bazı dosyalar yüklenemedi');
        }
      }

      // Form'u kapat ve temizle
      handleCloseForm();
      
      // Podcast listesini yenile
      fetchPodcasts();
    } catch (error: any) {
      console.error('Error saving podcast:', error);
      toast.error('Podcast kaydedilirken hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "px-3 py-1 text-sm font-medium rounded-full";
    switch (status) {
      case 'published':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'draft':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Podcast Yönetimi</h1>
            <p className="text-gray-600 mt-2">
              Podcast içeriklerini yönetin ve düzenleyin
            </p>
          </div>
          <button
            onClick={handleNewPodcast}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
          >
            <Plus className="h-5 w-5 mr-2" />
            Yeni Podcast
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 bg-primary-100 rounded-lg">
                <FileAudio className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Toplam Podcast</p>
                <p className="text-2xl font-bold text-gray-900">{podcasts.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <Eye className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Yayında</p>
                <p className="text-2xl font-bold text-gray-900">
                  {podcasts.filter(p => p.status === 'published').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Edit className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Taslak</p>
                <p className="text-2xl font-bold text-gray-900">
                  {podcasts.filter(p => p.status === 'draft').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Podcast Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Podcast Listesi</h2>
          </div>
          
          <div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Görsel
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Başlık
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Açıklama
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Durum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Oluşturulma
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                        <span className="ml-2 text-gray-600">Yükleniyor...</span>
                      </div>
                    </td>
                  </tr>
                ) : podcasts.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      Henüz podcast bulunmuyor.
                    </td>
                  </tr>
                ) : podcasts.map((podcast) => (
                  <tr key={podcast._id || podcast.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-12 w-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                        {podcast.thumbnailUrl ? (
                          <img 
                            src={podcast.thumbnailUrl} 
                            alt="Thumbnail" 
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <FileAudio className="h-6 w-6 text-gray-400" />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {typeof podcast.title === 'string' ? podcast.title : (podcast.title?.tr || 'İsimsiz Podcast')}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 max-w-xs truncate">
                        {(() => {
                          const summary = typeof podcast.summary === 'string' ? podcast.summary : (podcast.summary?.tr || '');
                          return summary.length > 50 ? summary.substring(0, 50) + '...' : summary;
                        })()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getStatusBadge(podcast.status)}>
                        {podcast.status === 'published' ? 'Yayında' : 'Taslak'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {podcast.createdAt ? new Date(podcast.createdAt).toLocaleDateString('tr-TR') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(podcast)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Düzenle"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(podcast)}
                          className="text-red-600 hover:text-red-900"
                          title="Sil"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Podcast Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[95vh] flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <FileAudio className="h-6 w-6 text-primary-600" />
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingPodcast ? 'Podcast Düzenle' : 'Yeni Podcast'}
                </h2>
              </div>
              <button
                onClick={handleCloseForm}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Language Tabs */}
              <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
                {(['tr', 'en', 'es'] as const).map((lang) => (
                  <button
                    key={lang}
                    onClick={() => setSelectedLanguage(lang)}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                      selectedLanguage === lang
                        ? 'bg-white text-primary-600 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {lang.toUpperCase()}
                  </button>
                ))}
              </div>

              <form 
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  handleSubmit(formData);
                }}
                className="space-y-6"
              >
                {/* Hidden inputs for all languages */}
                <input type="hidden" name="title_tr" value={formTitles.tr} />
                <input type="hidden" name="title_en" value={formTitles.en} />
                <input type="hidden" name="title_es" value={formTitles.es} />
                <input type="hidden" name="summary_tr" value={formSummaries.tr} />
                <input type="hidden" name="summary_en" value={formSummaries.en} />
                <input type="hidden" name="summary_es" value={formSummaries.es} />

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Başlık ({selectedLanguage.toUpperCase()})
                  </label>
                  <input
                    type="text"
                    value={formTitles[selectedLanguage]}
                    onChange={(e) => setFormTitles(prev => ({ ...prev, [selectedLanguage]: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Podcast başlığını girin"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Açıklama ({selectedLanguage.toUpperCase()})
                  </label>
                  <textarea
                    rows={4}
                    value={formSummaries[selectedLanguage]}
                    onChange={(e) => setFormSummaries(prev => ({ ...prev, [selectedLanguage]: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Podcast açıklamasını girin"
                  />
                </div>

                {/* Audio File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ses Dosyası
                  </label>
                  <div 
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary-400 transition-colors"
                    onClick={() => document.getElementById('podcast-audio-input')?.click()}
                  >
                    {audioPreview ? (
                      <div className="relative">
                        <div className="bg-gray-100 rounded-lg p-4 mb-4">
                          <FileAudio className="h-12 w-12 text-primary-600 mx-auto mb-2" />
                          <audio
                            src={audioPreview}
                            controls
                            className="w-full max-w-sm mx-auto"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveAudio();
                          }}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        <p className="text-sm text-gray-600 mt-2">
                          {selectedFiles.audio?.name || 'Mevcut ses dosyası'}
                        </p>
                      </div>
                    ) : (
                      <>
                        <FileAudio className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-sm text-gray-600 mb-2">
                          Ses dosyasını yükleyin
                        </p>
                        <button
                          type="button"
                          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Dosya Seç
                        </button>
                        <p className="text-xs text-gray-500 mt-2">
                          MP3, WAV formatlarında, maksimum 50MB
                        </p>
                      </>
                    )}
                  </div>
                  <input
                    id="podcast-audio-input"
                    type="file"
                    accept="audio/*"
                    onChange={handleAudioSelect}
                    className="hidden"
                  />
                </div>

                {/* Cover Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kapak Görseli
                  </label>
                  <div 
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary-400 transition-colors"
                    onClick={() => document.getElementById('podcast-image-input')?.click()}
                  >
                    {imagePreview ? (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="max-h-48 mx-auto rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage();
                          }}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-sm text-gray-600 mb-2">
                          Kapak görselini yükleyin
                        </p>
                        <button
                          type="button"
                          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Görsel Seç
                        </button>
                        <p className="text-xs text-gray-500 mt-2">
                          PNG, JPG, GIF formatlarında, maksimum 2MB
                        </p>
                      </>
                    )}
                  </div>
                  <input
                    id="podcast-image-input"
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Durum
                  </label>
                  <select
                    name="status"
                    defaultValue={editingPodcast?.status || 'draft'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="draft">Taslak</option>
                    <option value="published">Yayında</option>
                  </select>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCloseForm}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Kaydediliyor...' : (editingPodcast ? 'Güncelle' : 'Kaydet')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default Podcast;